import { ThemedCard } from '@/app/platform/core/layout/themes/components/ThemedCard.tsx';
import { ThemedLoadingSpinner } from '@/app/platform/core/layout/themes/components/ThemedLoadingSpinner.tsx';
import { ThemedText } from '@/app/platform/core/layout/themes/components/ThemedText.tsx';
import { TribeCard } from '@/app/features/tribes-projects/tribes/TribeCard.tsx';
import { useTheme } from '@/app/platform/core/layout/themes/ThemeContext.tsx';
import { useCurrentUserProfile } from '@/app/platform/functions/people/users/useCurrentUserProfile.ts';
import { useUserTribes } from '@/app/features/tribes-projects/tribes/useTribes.ts';
import type { TribeEntry } from '@/app/features/tribes-projects/tribes/tribes.query.types.ts';

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

function buildDedupedTribes(
  tribes: ReturnType<typeof useUserTribes>['tribes'],
): TribeEntry[] {
  const map = new Map<string, TribeEntry>();
  for (const row of tribes) {
    const existing = map.get(row.tribe_id);
    if (!existing) {
      map.set(row.tribe_id, {
        tribe_id: row.tribe_id,
        tribe_url_param_id: row.tribe_url_param_id,
        tribe_name: row.tribe_name,
        direct_position: row.via_represents ? null : row.position,
        represented_persons: row.via_represents
          ? [
              {
                first_name: row.person_first_name,
                last_name: row.person_last_name,
                position: row.position,
              },
            ]
          : [],
      });
    } else {
      if (!row.via_represents) {
        existing.direct_position = row.position;
      } else {
        existing.represented_persons.push({
          first_name: row.person_first_name,
          last_name: row.person_last_name,
          position: row.position,
        });
      }
    }
  }
  return Array.from(map.values());
}

const DashboardTribesTab: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user, isLoading: userLoading } = useCurrentUserProfile();
  const { tribes, loading: tribesLoading } = useUserTribes(user?.id || '', {
    enabled: !!user?.id,
  });

  const dedupedTribes = useMemo(() => buildDedupedTribes(tribes), [tribes]);

  if (userLoading || tribesLoading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}
      >
        <ThemedLoadingSpinner size="sm" />
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {dedupedTribes.map((tribe) => (
          <TribeCard
            key={tribe.tribe_id}
            tribe={tribe}
            onClick={(tr) => navigate(`/app/tribes/${tr.tribe_url_param_id}`)}
          />
        ))}
      </div>
      {dedupedTribes.length === 0 && (
        <ThemedCard variant="secondary">
          <ThemedText
            variant="secondary"
            size="medium"
            style={{ color: theme.colors.secondary }}
          >
            {t('tribes.empty')}
          </ThemedText>
        </ThemedCard>
      )}
    </div>
  );
};

export default DashboardTribesTab;
